
import {
  ComputedRef,
  defineComponent,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  watch
} from "vue";
import { useStore } from "vuex";
import { dispatchError } from "../../utils/error_handling";
import { Content } from "./helpers";
import ContentMain from "./ContentMain.vue";
import ContentMenu from "./ContentMenu.vue";
import { computed } from "@vue/reactivity";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { wrapLoading } from "../../utils/wrap_loading";
import {
  ContentProvider,
  ContentProviderType
} from "../../../../domain/entities/content-provider/content-provider";
import { ContentType } from "../../../../domain/entities/content/content";
import ContentAlertModal from "../../components/application/modals/ContentAlertModal.vue";
import ContentHeader from "../../components/application/ContentHeader.vue";

export default defineComponent({
  name: "ContentView",
  components: { ContentAlertModal, ContentMenu, ContentMain },
  props: {
    course_id: {
      type: String,
      default: ""
    },

    event_id: {
      type: String,
      default: ""
    },

    class_id: {
      type: String,
      default: ""
    },

    content_id: {
      type: String,
      required: true
    },
    questionnaire_id: {
      type: String
    }
  },

  setup({ content_id, course_id, event_id, class_id, questionnaire_id }) {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const courseID = parseInt(course_id);
    const eventID = parseInt(event_id);
    const classID = parseInt(class_id);
    const contentID = parseInt(content_id);
    const questionnaireID = parseInt(questionnaire_id ?? "0");

    const showRecommendOnDesktopModal: Ref<boolean> = ref(false);
    let lastContentID: number;

    onMounted(async () => {
      try {
        await wrapLoading(async () => {
          await loadContentProvider();
          await loadAllContents();
          await loadCurrentContent();
        });
      } catch (e) {
        console.error("[onMounted] Error ", e);
        await dispatchError(e);
      }
    });

    const loadContentProvider = async (): Promise<void> => {
      try {
        const eventProvider = {
          type: ContentProviderType.EVENT,
          ids: { providerID: eventID, classID: classID }
        };
        const courseProvider = {
          type: ContentProviderType.COURSE,
          ids: { providerID: courseID }
        };
        const provider = course_id ? courseProvider : eventProvider;
        await store.dispatch("content_module/loadContentProvider", provider);
      } catch (e) {
        console.error("[loadContentProvider] Error dispatch", e);
        throw e;
      }
    };

    const loadAllContents = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/loadAllContents");
      } catch (e) {
        console.error("[loadContents] Error dispatch ", e);
        await dispatchError(e);
      }
    };

    const loadCurrentContent = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/setCurrentContent", contentID);
      } catch (e) {
        console.error("[loadCurrentContent] Error ", e);
        throw e;
      }
    };

    const currentContent: ComputedRef<Content> = computed(
      () => store.getters["content_module/currentContent"]
    );

    watch(
      () => currentContent.value,
      async (value, oldValue) => await onUpdateCurrentContent(oldValue, value)
    );

    const onUpdateCurrentContent = async (
      oldValue: Content,
      value: Content
    ): Promise<void> => {
      try {
        await initializeDependentModules();
        recommendOnDesktopHandler();
        if (oldValue === null) {
          const currentRoute = router.currentRoute.value;
          if (!currentRoute) return;
          if (
            currentRoute.name === "event_questionnaire" ||
            currentRoute.name === "course_questionnaire"
          ) {
            await router.push({
              name: currentRoute.name,
              params: {
                content_id: contentID,
                questionnaire_id: questionnaireID,
                page: currentRoute.params.page
              }
            });
          }
        } else {
          await updateContentRoute();
        }
      } catch (e) {
        console.error("[onUpdateCurrentContent] Error updateContentRoute ", e);
        await dispatchError(e);
      }
    };

    const initializeDependentModules = async (): Promise<void> => {
      try {
        switch (currentContent.value.type) {
          case ContentType.EXERCISE:
            await initializeExerciseModule();
            break;
        }
      } catch (e) {
        console.error("[initializeExerciseModule] Error ", e);
        throw e;
      }
    };

    const initializeExerciseModule = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/exercise/initializeModule");
      } catch (e) {
        console.error("[initializeExerciseModule] Error ", e);
        throw e;
      }
    };

    const updateContentRoute = async () => {
      try {
        const contentProvider = store.getters[
          "content_module/contentProvider"
        ] as ContentProvider;
        switch (contentProvider.getType()) {
          case ContentProviderType.EVENT:
            await router.push({
              name: "event_content",
              params: {
                event_id: eventID,
                class_id: classID,
                content_id: currentContent.value.id
              },
              query: { ...route.query }
            });
            break;
          case ContentProviderType.COURSE:
            await router.push({
              name: "course_content",
              params: {
                course_id: courseID,
                content_id: currentContent.value.id
              },
              query: { ...route.query }
            });
            break;
        }
      } catch (e) {
        console.error("[updateContentRoute] Error push", e);
        throw e;
      }
    };

    const recommendOnDesktopHandler = () => {
      if (
        currentContent.value.recommendOnDesktop &&
        lastContentID !== currentContent.value.id &&
        document.body.offsetWidth < 780
      ) {
        showRecommendOnDesktopModal.value = true;
        lastContentID = currentContent.value.id;
      }
    };

    const onCloseRecommendOnDesktopModal = async () => {
      showRecommendOnDesktopModal.value = false;
    };

    onUnmounted(async () => {
      await store.dispatch("content_module/clearCurrentContent");
    });

    return {
      currentContent,
      showRecommendOnDesktopModal,
      onCloseRecommendOnDesktopModal
    };
  }
});
